import { useMemo, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import ReactTimeAgo from "react-time-ago";
import Link from "next/link";

import {
  Page,
  FilterBar,
  Fields,
  FieldType,
  Toolbar,
  FixedToolbarItem,
  FlexToolbarItem,
  RequestUsageBar,
  FilterBadge,
  FilterCriteria,
} from "@bagel-web/components";
import {
  APIError,
  APIKeySelect,
  APIKeySelectProps,
  getUserContext,
  isFilterableMetadataKey,
  isFilterableMetadataValue,
  LocalStorageKeys,
  metadataClause,
  metadataFilterClauses,
  metadataFilterKeys,
  Page as PageType,
  PaginatedSearch,
  Paginator,
  useFilters,
  util,
} from "@bagel-web/common";
import type { SessionSummary } from "api/types";
import { fetchAPIKeys, fetchSessions } from "api";
import NoProjectsZeroState from "components/NoProjectsZeroState";

const ComposedAPIKeySelect = (
  props: Omit<APIKeySelectProps, "fetchAPIKeys">
) => {
  return <APIKeySelect {...props} fetchAPIKeys={fetchAPIKeys} />;
};

type SearchFilters = {
  sessionId: string | null;
  metadata: string | null;
  apiKeyId: string | null;
  fromDateTime: number | null;
  toDateTime: number | null;
  subject: string | null;
  sessionCorrelationId: string | null;
  minRequests: number | null;
};

const sessionQueries = {
  search: (filters: PaginatedSearch<SearchFilters>) => [
    "sessions",
    "search",
    filters,
  ],
};

function DumpMetadata({
  sessionId,
  metadata,
  onFilterByPair,
}: {
  sessionId: string;
  metadata: Record<string, string>;
  onFilterByPair: (key: string, value: string) => void;
}) {
  return (
    <div className="d-flex flex-wrap gap-1">
      {Object.entries(metadata).map(([key, value]) => {
        const filterable =
          isFilterableMetadataKey(key) && isFilterableMetadataValue(value);
        // The tooltip mirrors the chip content so a truncated value can be
        // read in full, styled the same way.
        const pair = (
          <>
            <em>{key}</em>: <code>{value}</code>
          </>
        );
        return (
          <FilterBadge
            key={key}
            data-testid={`session-metadata-badge-${sessionId}-${key}`}
            // Cap chip width so arbitrary metadata values cannot blow out
            // the table; FilterBadge truncates the overflow with an ellipsis.
            style={{ maxWidth: "20rem" }}
            tooltip={pair}
            onClick={filterable ? () => onFilterByPair(key, value) : undefined}
            content={pair}
          />
        );
      })}
    </div>
  );
}

function SessionRow({
  session,
  onSetFilters,
  onAddMetadataFilter,
}: {
  session: SessionSummary;
  onSetFilters: (criteria: FilterCriteria<string>) => void;
  onAddMetadataFilter: (key: string, value: string) => void;
}) {
  return (
    <tr>
      <td className="text-nowrap">
        <ReactTimeAgo
          date={new Date(session.last_activity_at * 1000)}
          timeStyle="round"
        />
      </td>
      <td className="text-nowrap">
        <ReactTimeAgo
          date={new Date(session.created * 1000)}
          timeStyle="round"
        />
      </td>
      <td>
        <Link href={`/sessions/${session.id}`}>
          <code>{session.id}</code>
        </Link>
      </td>
      <td className="text-nowrap">
        {/*
          Identity: auth identity is mutually exclusive per session. An
          API-key session shows the key; a runtime-token session shows its
          subject and (when set) its correlation id. Each is a click-to-filter
          FilterBadge.
        */}
        {session.api_key_id ? (
          <FilterBadge
            data-testid={`session-api-key-button-${session.id}`}
            onClick={() =>
              session.api_key_id &&
              onSetFilters({ apiKeyId: { value: session.api_key_id } })
            }
            content={session.api_key_name}
          />
        ) : (
          <div className="d-flex flex-column align-items-start gap-1">
            {session.subject && (
              <FilterBadge
                data-testid={`session-subject-button-${session.id}`}
                onClick={() =>
                  session.subject &&
                  onSetFilters({ subject: { value: session.subject } })
                }
                content={<code>{session.subject}</code>}
              />
            )}
            {session.session_correlation_id && (
              <FilterBadge
                data-testid={`session-correlation-button-${session.id}`}
                onClick={() =>
                  session.session_correlation_id &&
                  onSetFilters({
                    sessionCorrelationId: {
                      value: session.session_correlation_id,
                    },
                  })
                }
                content={<code>{session.session_correlation_id}</code>}
              />
            )}
          </div>
        )}
      </td>
      <td>
        <DumpMetadata
          sessionId={session.id}
          metadata={session.metadata}
          onFilterByPair={onAddMetadataFilter}
        />
      </td>
      <td>
        {session.max_requests ? (
          <RequestUsageBar max={session.max_requests} used={session.requests} />
        ) : (
          session.requests
        )}
      </td>
    </tr>
  );
}

function SessionsTable({
  sessions,
  onSetFilters,
  onAddMetadataFilter,
}: {
  sessions: PageType<SessionSummary>;
  onSetFilters: (criteria: FilterCriteria<string>) => void;
  onAddMetadataFilter: (key: string, value: string) => void;
}) {
  if (sessions.items.length === 0) {
    return <Alert data-testid="no-sessions-alert">No matching sessions.</Alert>;
  }

  return (
    <Table data-testid="sessions-table">
      <thead>
        <tr>
          <th className="text-nowrap">Last Activity</th>
          <th className="text-nowrap">Created</th>
          <th>Session ID</th>
          <th className="text-nowrap">Identity</th>
          <th>Metadata</th>
          <th>Requests</th>
        </tr>
      </thead>
      <tbody>
        {sessions.items.map((r) => {
          return (
            <SessionRow
              key={r.id}
              session={r}
              onSetFilters={onSetFilters}
              onAddMetadataFilter={onAddMetadataFilter}
            />
          );
        })}
      </tbody>
    </Table>
  );
}

function SessionsLoader() {
  const queryClient = useQueryClient();
  const filterFields = useMemo<Fields>(
    () => [
      {
        name: "sessionId",
        label: "Session ID",
        showMonoValue: true,
        type: FieldType.String,
        colSpan: 1,
      },
      {
        name: "apiKeyId",
        label: "API Key",
        showMonoValue: true,
        Component: ComposedAPIKeySelect,
        type: FieldType.String,
        colSpan: 1,
      },
      {
        name: "subject",
        label: "Subject",
        showMonoValue: true,
        type: FieldType.String,
        colSpan: 1,
      },
      {
        name: "sessionCorrelationId",
        label: "Correlation ID",
        showMonoValue: true,
        type: FieldType.String,
        colSpan: 1,
      },
      {
        name: "metadata",
        label: "Metadata",
        showMonoValue: true,
        type: FieldType.String,
        colSpan: 1,
        hint: ['key:"value"', 'key2:"value2"'],
        // One chip per clause; clicking a chip removes just that clause.
        splitValue: metadataFilterClauses,
        // The full clause can get huge, so each chip shows only its key.
        // Fall back to the raw value if nothing parses.
        formatValue: (value) => {
          const keys = metadataFilterKeys(value || "");
          return keys.length ? keys.join(", ") : value;
        },
      },
      {
        name: "minRequests",
        label: "Min # of Requests",
        type: FieldType.Number,
        colSpan: 1,
      },
      {
        name: "fromDateTime",
        label: "From",
        type: FieldType.DateTime,
        formatValue: (value) => util.formatDate(new Date(value as string)),
      },
      {
        name: "toDateTime",
        label: "To",
        type: FieldType.DateTime,
        formatValue: (value) => util.formatDate(new Date(value as string)),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient]
  );

  const { filterCriteria, handleSetFilters, handleClearFilter } = useFilters(
    filterFields,
    LocalStorageKeys.Filters.SESSIONS
  );

  const handleAddMetadataFilter = (key: string, value: string) => {
    const clause = metadataClause(key, value);
    const existing = filterCriteria.metadata?.value;
    if (existing && metadataFilterClauses(existing).includes(clause)) {
      return;
    }
    handleSetFilters({
      ...filterCriteria,
      metadata: { value: existing ? `${existing} ${clause}` : clause },
    });
  };

  const search: SearchFilters = useMemo(() => {
    return {
      sessionId: filterCriteria.sessionId?.value || null,
      metadata: filterCriteria.metadata?.value || null,
      apiKeyId: filterCriteria.apiKeyId?.value || null,
      subject: filterCriteria.subject?.value || null,
      sessionCorrelationId: filterCriteria.sessionCorrelationId?.value || null,
      minRequests:
        (filterCriteria.minRequests?.value &&
          util.convertStringToInt(filterCriteria.minRequests.value)) ||
        null,
      fromDateTime:
        (filterCriteria.fromDateTime &&
          util.getTimestampFromDateString(
            filterCriteria.fromDateTime?.value
          )) ||
        null,
      toDateTime:
        (filterCriteria.toDateTime &&
          util.getTimestampFromDateString(filterCriteria.toDateTime?.value)) ||
        null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterCriteria)]);

  const [isFirstPage, setIsFirstPage] = useState(true);
  const [paginatedSearch, setPaginatedSearch] = useState<
    PaginatedSearch<SearchFilters>
  >({
    ...search,
    startsAfter: null,
  });

  const { isPending, isError, data, error, refetch, isRefetching } = useQuery({
    queryKey: sessionQueries.search(paginatedSearch),
    queryFn: () => fetchSessions(paginatedSearch),
  });

  const getErrorMessage = (error: APIError): string => {
    if (error.status === 400 && error?.error.type === "invalid_request") {
      if (error?.error.message.includes("Invalid metadata query")) {
        return "Metadata filter is invalid. Format should be space-separated 'key:value' pairs; quote values containing spaces or special characters. Keys may not contain special characters other than underscores";
      }
    }
    return error.message;
  };

  let result: React.ReactElement;
  if (isPending) {
    result = <Spinner data-testid="sessions-loader-spinner" />;
  } else if (isError) {
    result = (
      <Alert variant="warning">
        Could not load sessions: {getErrorMessage(error as APIError)}.
      </Alert>
    );
  } else {
    result = (
      <SessionsTable
        sessions={data}
        onSetFilters={handleSetFilters}
        onAddMetadataFilter={handleAddMetadataFilter}
      />
    );
  }

  return (
    <>
      <Toolbar>
        <FixedToolbarItem>
          <h2>Sessions</h2>
        </FixedToolbarItem>

        <FlexToolbarItem>
          <FilterBar
            criteria={filterCriteria}
            fields={filterFields}
            onSetFilters={handleSetFilters}
            onClear={handleClearFilter}
          />
        </FlexToolbarItem>
        <FixedToolbarItem>
          {" "}
          <Button
            onClick={() => refetch()}
            disabled={!isFirstPage || isRefetching}
            title="Refresh"
          >
            {isRefetching && <Spinner size="sm" />}
            {!isRefetching && <i className="bi-arrow-clockwise" />}
          </Button>
        </FixedToolbarItem>
      </Toolbar>

      {result}

      <Paginator
        search={search}
        currentPage={data || null}
        onChangePaginatedSearch={({ paginatedSearch, isFirstPage }) => {
          setPaginatedSearch(paginatedSearch);
          setIsFirstPage(isFirstPage);
        }}
      />
    </>
  );
}

function SessionsPage() {
  const hasProjects = getUserContext().projects.length > 0;
  return (
    <Page title="Sessions">
      <Page.Body>
        {hasProjects ? <SessionsLoader /> : <NoProjectsZeroState />}
      </Page.Body>
    </Page>
  );
}

export default SessionsPage;
